import { FC, useEffect } from 'react'
import { RenderIf } from 'components/RenderIf'
import { useGetProductPrice } from 'featureSliced/shared/hooks/useGetProductPrice/indexT'
import { Button } from 'featureSliced/entities/Button'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'
import { AmountHandler } from 'featureSliced/widgets/AmountHandler'
import { getAmountConfig } from 'featureSliced/shared/utils/weightSteps'
import { handleLocalStorage } from 'featureSliced/shared/hooks/handleLocalStorage'
import {
  getItemLeadDays,
  isItemLeadDaysExceeded,
} from 'featureSliced/shared/utils/itemLeadDays'

import { IProduct } from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import { QRMBucketSlice } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer'
import { TableStatuses } from 'featureSliced/shared/store/reducers/QRMenuTableReducer/types'

import { Divider, FooterContainer } from '../ComonProduct/styled'
import { KitchenInfoItemType } from '../types'
import { ProductTemplateIdType } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'

type AddCommonProductFooterProps = {
  product: IProduct
  productTemplateId: ProductTemplateIdType
  amountState: [number, (value: number) => void]
  closeProduct: () => void
  selectedKitchenInfo: KitchenInfoItemType
  initAmount: number
}

export const AddCommonProductFooter: FC<AddCommonProductFooterProps> = ({
  product,
  productTemplateId,
  amountState,
  closeProduct,
  selectedKitchenInfo,
  initAmount,
}) => {
  // Hooks
  const t = useTranslations()
  const dispatch = useMyDispatch()
  const addProductToBucket = QRMBucketSlice.actions.addProductToBucket
  const selectedQRMProducts = useMySelector((state) => state.bucket.products)
  const removeProductFromBucket = QRMBucketSlice.actions.removeProductFromBucket
  const { getProductPrice } = useGetProductPrice()

  // Store
  const tableStatus = useMySelector((state) => state.qrMenuTable.tableStatus)
  const bookingVariant = useMySelector((state) => state.app.menu.bookingVariant)
  const disabledByMobileWaiter = useMySelector(
    (state) => state.app.menu.disabledByMobileWaiter
  )

  // State
  const [currentAmount, setCurrentAmount] = amountState

  const uniqueId = `${productTemplateId}_${product.id}_${JSON.stringify(
    selectedKitchenInfo
  )}`
  const productFromBucket = selectedQRMProducts?.find(
    (item) => item.uniqueId === uniqueId
  )
  const isProductInBucket = Boolean(productFromBucket)
  const amountConfig = getAmountConfig(product.unitSellCode)
  const isLeadDaysExceeded = isItemLeadDaysExceeded({
    leadDays: getItemLeadDays(product.ClickCollectLeadDaysCount),
    pickUpTime: handleLocalStorage('pickUpTime'),
  })
  const clickCollectMin = product.ClickCollectOrderMin
  const hasClickCollectMin =
    clickCollectMin !== null && clickCollectMin !== undefined
  const defaultMinValue = hasClickCollectMin
    ? clickCollectMin
    : amountConfig.minAmount
  const minValue = isProductInBucket ? 0 : defaultMinValue
  const nextValueAfterZero = hasClickCollectMin
    ? clickCollectMin
    : amountConfig.minAmount

  // useEffect
  useEffect(() => {
    if (initAmount) {
      setCurrentAmount(initAmount)
    }
  }, [initAmount])

  useEffect(() => {
    if (!isProductInBucket && currentAmount < defaultMinValue) {
      setCurrentAmount(defaultMinValue)
    }
  }, [isProductInBucket, currentAmount, defaultMinValue, setCurrentAmount])

  const addToBucket = () => {
    const { id, categoryId, unitSellCode } = product
    const productToAdd = {
      uniqueId,
      productId: id,
      productTemplateId,
      categoryId,
      count: currentAmount,
      kitchenInfo: JSON.stringify(selectedKitchenInfo || {}),
      price: getProductPrice(product, productTemplateId).finalPrice,
      addedAsWeight: unitSellCode === 2 || unitSellCode === 3,
    }
    if (currentAmount) {
      dispatch(addProductToBucket(productToAdd))
    } else {
      dispatch(removeProductFromBucket(uniqueId))
    }
    closeProduct()
  }

  // No amount between 0 and the order minimum is selectable: a step that would land
  // there stops at the minimum, and only a step taken FROM the minimum empties the
  // line (0, which the footer button turns into "remove").
  const handleDecreaseAmount = (value: number) => {
    if (value > 0 && value < defaultMinValue) {
      setCurrentAmount(currentAmount > defaultMinValue ? defaultMinValue : 0)
      return
    }
    setCurrentAmount(value)
  }

  const handleIncreaseAmount = (value: number) => {
    if (currentAmount === 0) {
      if (isProductInBucket) {
        setCurrentAmount(nextValueAfterZero)
        return
      }
      if (!isProductInBucket && currentAmount < defaultMinValue) {
        setCurrentAmount(defaultMinValue)
        return
      }
    }
    setCurrentAmount(value)
  }

  return (
    <RenderIf
      key={`${navigator.onLine}${bookingVariant}${disabledByMobileWaiter}${tableStatus}${product.soldOut}${isLeadDaysExceeded}`}
      condition={
        navigator.onLine &&
        (bookingVariant === 2 || process.env.REACT_APP_IS_CLIC === '1') &&
        !disabledByMobileWaiter &&
        tableStatus !== TableStatuses.BUSY &&
        !product.soldOut &&
        !isLeadDaysExceeded
      }
    >
      <Divider stacked={Boolean(amountConfig.coarseStep)} />
      <FooterContainer stacked={Boolean(amountConfig.coarseStep)}>
        <AmountHandler
          multiplier={amountConfig.multiplier}
          step={amountConfig.step}
          coarseStep={amountConfig.coarseStep}
          fineLabel={amountConfig.fineLabel}
          coarseLabel={amountConfig.coarseLabel}
          unit={amountConfig.unit}
          currentValue={currentAmount}
          onDecrease={handleDecreaseAmount}
          onIncrease={handleIncreaseAmount}
          minValue={minValue} // only needed for C&C
          maxValue={product.ClickCollectOrderMax ?? undefined} // only needed for C&C
        />
        <Button
          buttonType="common"
          disabled={tableStatus === TableStatuses.BUSY}
          width="100%"
          onClick={addToBucket}
        >
          {currentAmount ? t('products.addOrder') : t('buttons.remove')}
        </Button>
      </FooterContainer>
    </RenderIf>
  )
}
